import React, { FunctionComponent } from 'react'
import { Link } from 'react-router-dom'
import useTranslation from '../../hooks/useTranslation'
import EquipmentGroup from '../../entities/EquipmentGroup'
import urlifyGroupName from '../../helpers/urlifyGroupName'
import OverviewEquipmentTable from './OverviewEquipmentTable'
import style from './overviewEquipmentGroups.css'
import sortEquipmentGroupsByOrdering from '../../helpers/sortEquipmentGroups'

export type Props = {
    selectedEquipmentGroups: EquipmentGroup[]
    deselectEquipment: Function
    openInfoModal: Function
    viewProperties: Record<string, string>[]
    equipmentGroupOrdering: string[]
}

const OverviewEquipmentGroups: FunctionComponent<Props> = (props) => {
    const {
        selectedEquipmentGroups,
        deselectEquipment,
        openInfoModal,
        viewProperties,
        equipmentGroupOrdering,
    } = props
    const { t } = useTranslation()

    const standardGroupId = 'standards'

    const hideStandardOnOverviewPage = viewProperties && viewProperties['hideStandardOnOverview'] === 'true'

    const isStandardGroupPresent = selectedEquipmentGroups.filter(g => g.id === standardGroupId)?.length

    const allSelectedPackagesContent = selectedEquipmentGroups.flatMap((equipmentGroup) => {
        if (!equipmentGroup) {
            return []
        }
        const { equipmentFamilies } = equipmentGroup
        return equipmentFamilies.flatMap((equipmentFamily) => equipmentFamily.equipments)
            .filter((equipment) => (equipment.isPackage && equipment.selected))
            .flatMap((equipment) => equipment.content)
            .map(content => content.id)
    })

    const sortedSelectedEquipmentGroups = sortEquipmentGroupsByOrdering(selectedEquipmentGroups, equipmentGroupOrdering)

    const contentForStealthGroup = (equipmentGroup) => {
        const { equipmentFamilies } = equipmentGroup

        if (!equipmentFamilies) {
            return null
        }

        const includedInPackageEquipmentIds = equipmentFamilies.flatMap((equipmentFamily) => equipmentFamily.equipments)
            .filter((equipment) => equipment.isPackage)
            .filter((equipment) => (equipment.isStandard && equipment.replacedBy == null) || (!equipment.isStandard && equipment.selected))
            .flatMap((equipment) => equipment.content)
            .map(content => content.id)

        const allEquipmentsInGroup = equipmentFamilies
            .flatMap((equipmentFamily) => equipmentFamily.equipments)

        if (allEquipmentsInGroup.length === 0) {
            return null
        }

        const content = equipmentFamilies.map(equipmentFamily => {
                let equipments = isStandardGroupPresent && equipmentGroup.id !== standardGroupId
                    ? equipmentFamily.equipments.filter(eq => !eq.isStandard)
                    : equipmentFamily.equipments
                equipments = equipments.filter(eq => !includedInPackageEquipmentIds.includes(eq.id) && !allSelectedPackagesContent.includes(eq.id))
                return (equipments.length === 0)
                    ? undefined
                    : (
                        <div key={equipmentFamily.id}>
                            <OverviewEquipmentTable
                                equipments={equipments}
                                url={undefined}
                                deselectEquipment={deselectEquipment}
                                openInfoModal={openInfoModal}
                            />
                        </div>
                    )
            })

        return (
            <section
                key={equipmentGroup.id}
                data-testid={`equipment-group-${equipmentGroup.id?.replace(/\\/g, '.')}`}
            >
                <h3 className={style.title}>
                    {t(`navigation.${equipmentGroup.id}`)}
                </h3>
                {content}
            </section>
        )
    }

    const contentForGroup = (equipmentGroup) => {
        const url = `${urlifyGroupName(equipmentGroup.id)}`
        const { equipmentFamilies } = equipmentGroup

        if (!equipmentFamilies) {
            return null
        }

        const includedInPackageEquipmentIds = equipmentFamilies.flatMap((equipmentFamily) => equipmentFamily.equipments)
            .filter((equipment) => equipment.isPackage)
            .filter((equipment) => (equipment.isStandard && equipment.replacedBy == null) || (!equipment.isStandard && equipment.selected))
            .flatMap((equipment) => equipment.content)
            .map(content => content.id)

        const allEquipmentsInGroup = equipmentFamilies
            .flatMap((equipmentFamily) => equipmentFamily.equipments)

        const content = allEquipmentsInGroup.length !== 0
            ? equipmentFamilies.map(equipmentFamily => {
                let equipments = isStandardGroupPresent && equipmentGroup.id !== standardGroupId
                    ? equipmentFamily.equipments.filter(eq => !eq.isStandard)
                    : equipmentFamily.equipments
                equipments = equipments.filter(eq => !includedInPackageEquipmentIds.includes(eq.id) && !allSelectedPackagesContent.includes(eq.id))
                return (equipments.length === 0)
                    ? undefined
                    : (
                        <div key={equipmentFamily.id}>
                            <h3>{equipmentFamily.name}</h3>
                            <OverviewEquipmentTable
                                equipments={equipments}
                                url={url}
                                deselectEquipment={deselectEquipment}
                                openInfoModal={openInfoModal}
                            />
                        </div>
                    )
            })
            : (
                <p>
                    <Link to={url} className={style.fallbackLink}>
                        {t('overviewSelection.fallbackLink')}
                    </Link>
                </p>
            )

        return (
            <section
                key={equipmentGroup.id}
                data-testid={`equipment-group-${equipmentGroup.id?.replace(/\\/g, '.')}`}
            >
                <h2 className={style.title}>
                    {t(`navigation.${equipmentGroup.id}`)}
                </h2>
                {content}
            </section>
        )
    }

    const selectedEquipmentGroupElements = sortedSelectedEquipmentGroups.map((equipmentGroup) => {
        if (!equipmentGroup) {
            return null
        }
        if (equipmentGroup.id === standardGroupId && hideStandardOnOverviewPage) {
            return null
        }
        if (equipmentGroup.id === "stealth") {
            return contentForStealthGroup(equipmentGroup)
        } else {
            return contentForGroup(equipmentGroup)
        }
    })

    return (
        <>
            {selectedEquipmentGroupElements}
        </>
    )
}

export default OverviewEquipmentGroups
